import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Check, PencilRuler, Sparkles, TriangleAlert, Wand2 } from 'lucide-react'
import { useState } from 'react'

import { annotationQuery, countLints, publishAnnotation, type AnnotationEnvelope } from '@/api/annotations'
import { keys } from '@/api/keys'
import { stageKeys, type StageAnnotationState, type StageRow } from '@/api/stages'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { kindColour, kindLabel, useVocab, type VocabLike } from '@/features/annotator/vocab'
import { isStageBody } from '@/stores/editorStore'
import type { StageBody } from '@/schema'

import { featureLabel, placesState, presentFeatures } from './words'

/**
 * The Places tab: what the studio knows about this picture — where characters can walk, the
 * water, the sky, the spots they use (a log, a door) and the ways in and out — drawn over the
 * picture and listed in words, with the one big next step: **Edit places** (the stage annotator,
 * which brings you back here). When the latest places are not yet what stories use, one button
 * publishes them.
 */

export interface PlacesTabProps {
  uid: string
  row: StageRow | null
  picture: string
}

const EDGE_WORDS: Record<string, string> = { left: 'the left edge', right: 'the right edge', top: 'the top', bottom: 'the bottom' }

export function PlacesTab({ uid, row, picture }: PlacesTabProps) {
  const qc = useQueryClient()
  const vocab = useVocab()
  // Read fresh every time the tab shows: the annotator may have changed it a moment ago.
  const doc = useQuery({ ...annotationQuery(uid), staleTime: 0 })
  const env = doc.data
  const body: StageBody | null = env && isStageBody(env.body) ? env.body : null
  const state = env ? stateOf(env) : (row?.annotation ?? null)

  const publish = useMutation({
    mutationFn: () => publishAnnotation(uid),
    onSuccess: (next) => {
      qc.setQueryData(keys.annotation.doc(uid), next)
      void qc.invalidateQueries({ queryKey: [...stageKeys.all(), 'list'] })
      void qc.invalidateQueries({ queryKey: stageKeys.one(uid) })
      toast.success('Stories use these places now')
    },
    onError: (err: unknown) => {
      const e = err as { message?: string; details?: { lints?: { message: string; fix_hint?: string }[] } }
      const first = e.details?.lints?.[0]
      toast.error(first ? first.message : (e.message ?? 'That did not work.'), first?.fix_hint ? { description: first.fix_hint } : {})
    },
  })

  const editSearch = { return: `/backgrounds/${uid}` }
  const s = state ? placesState(state) : null
  const canPublish = state !== null && state.lint_errors === 0 && (state.published_rev === null || state.rev > state.published_rev)

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-testid="places-tab">
      <div className="flex min-w-0 flex-col gap-2">
        <PlacesPicture picture={picture} body={body} vocab={vocab} />
        <p className="text-xs text-ink-3">{doc.isPending ? 'Reading the places…' : body ? 'Coloured shapes are the places the studio knows; dots are spots characters use.' : 'Nothing is marked on this picture yet.'}</p>
      </div>

      <div className="flex min-w-0 flex-col gap-5">
        {s ? (
          <div className="flex flex-col gap-1" data-testid="places-state">
            <div className="flex items-center gap-2">
              <Badge tone={s.tone === 'ok' ? 'ok' : s.tone === 'danger' ? 'danger' : 'warn'} dot>
                {s.label}
              </Badge>
              {state && state.lint_warnings > 0 && state.lint_errors === 0 ? <span className="text-xs text-ink-3">{state.lint_warnings === 1 ? '1 thing worth a look' : `${state.lint_warnings} things worth a look`}</span> : null}
            </div>
            <p className="text-sm text-ink-2">{s.hint}</p>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="primary" size="lg">
            <Link to="/annotate/$kind/$" params={{ kind: 'stage', _splat: uid }} search={editSearch} data-testid="edit-places">
              <PencilRuler aria-hidden />
              Edit places
            </Link>
          </Button>
          <Button asChild variant="secondary" size="lg">
            <Link to="/annotate/$kind/$" params={{ kind: 'stage', _splat: uid }} search={{ ...editSearch, mode: 'simple' }} data-testid="quick-fix">
              <Wand2 aria-hidden />
              Quick fix
            </Link>
          </Button>
          {canPublish ? (
            <Button variant="secondary" size="lg" onClick={() => publish.mutate()} loading={publish.isPending} data-testid="publish-places">
              {publish.isPending ? null : <Sparkles aria-hidden />}
              Use these places in stories
            </Button>
          ) : null}
        </div>
        <p className="-mt-3 text-xs text-ink-3">“Quick fix” asks three simple questions: where to walk, where the ground meets the sky, and where characters come in.</p>

        {body ? <PlaceLists body={body} vocab={vocab} /> : row ? <RowSummary row={row} /> : null}

        {body ? <Checks body={body} /> : null}
      </div>
    </div>
  )
}

function stateOf(env: AnnotationEnvelope): StageAnnotationState {
  const lints = isStageBody(env.body) ? countLints(env.body.lints) : { errors: 0, warnings: 0 }
  return { status: env.status, rev: env.rev, published_rev: env.published_rev, lint_errors: lints.errors, lint_warnings: lints.warnings }
}

/** The picture with the regions (translucent), the horizon and the spots drawn over it. */
function PlacesPicture({ picture, body, vocab }: { picture: string; body: StageBody | null; vocab: VocabLike }) {
  const [broken, setBroken] = useState(false)
  const horizon = body?.depth.mode === 'horizon' ? body.depth.horizon_y : undefined
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-paper-3" data-testid="places-picture">
      {!broken ? <img src={picture} alt="" draggable={false} onError={() => setBroken(true)} className="absolute inset-0 size-full object-cover" /> : null}
      {body ? (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full" aria-hidden>
          {body.regions.map((r) => (
            <polygon
              key={r.id}
              points={r.poly.outer.map(([x, y]) => `${x},${y}`).join(' ')}
              fill={kindColour(vocab, r.kind)}
              fillOpacity={0.28}
              stroke={kindColour(vocab, r.kind)}
              strokeWidth={0.35}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {horizon !== undefined ? <line x1={0} x2={100} y1={horizon} y2={horizon} stroke="oklch(0.85 0.15 90)" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" /> : null}
        </svg>
      ) : null}
      {body?.anchors.map((a) => (
        <span
          key={a.id}
          className="absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-card"
          style={{ left: `${a.at[0]}%`, top: `${a.at[1]}%`, background: kindColour(vocab, a.kind) }}
          title={a.label ?? kindLabel(vocab, a.kind)}
        />
      ))}
    </div>
  )
}

function PlaceLists({ body, vocab }: { body: StageBody; vocab: VocabLike }) {
  const regionKinds = [...new Set(body.regions.map((r) => r.kind))]
  const ways = body.portals.map((p) => (p.edge ? `${kindLabel(vocab, p.kind)} at ${EDGE_WORDS[p.edge] ?? p.edge}` : kindLabel(vocab, p.kind)))
  return (
    <div className="flex flex-col gap-4" data-testid="place-lists">
      <ListBlock title="What this place has" empty="Nothing marked yet — “Edit places” to show where to walk.">
        {regionKinds.map((k) => (
          <Swatch key={k} colour={kindColour(vocab, k)} label={kindLabel(vocab, k)} />
        ))}
      </ListBlock>
      <ListBlock title="Spots characters use" empty="No spots yet.">
        {body.anchors.map((a) => (
          <Swatch key={a.id} colour={kindColour(vocab, a.kind)} label={a.label ?? kindLabel(vocab, a.kind)} />
        ))}
      </ListBlock>
      <ListBlock title="Ways in and out" empty="No ways in or out — characters appear and vanish.">
        {ways.map((w, i) => (
          <li key={`${i}:${w}`} className="text-sm text-ink-2">
            {w}
          </li>
        ))}
      </ListBlock>
      <p className="text-sm text-ink-2">
        {body.depth.mode === 'horizon' ? (
          <>
            <Check className="mr-1 inline size-4 text-ok" aria-hidden />
            The horizon is set, so characters get smaller as they walk away.
          </>
        ) : (
          'Sizes come from a depth guide, not a horizon line.'
        )}
      </p>
    </div>
  )
}

/** Before the annotation loads (or when it cannot): the list's own words. */
function RowSummary({ row }: { row: StageRow }) {
  const features = presentFeatures(row.features)
  return (
    <div className="flex flex-col gap-4">
      <ListBlock title="What this place has" empty="Nothing marked yet.">
        {features.map((f) => (
          <li key={f} className="text-sm text-ink-2">
            {featureLabel(f)}
          </li>
        ))}
      </ListBlock>
      <ListBlock title="Spots characters use" empty="No spots yet.">
        {row.anchors.map((a) => (
          <li key={a} className="text-sm text-ink-2">
            {a.replace(/_/g, ' ')}
          </li>
        ))}
      </ListBlock>
    </div>
  )
}

function Checks({ body }: { body: StageBody }) {
  const lints = body.lints.filter((l) => l.severity === 'error' || l.severity === 'warn')
  if (lints.length === 0) return null
  return (
    <section className="flex flex-col gap-2" data-testid="place-checks">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-3">Worth a look</h3>
      <ul className="flex flex-col gap-1.5">
        {lints.slice(0, 6).map((l, i) => (
          <li key={`${i}:${l.code}`} className="flex gap-2 text-sm">
            <TriangleAlert className={l.severity === 'error' ? 'mt-0.5 size-4 shrink-0 text-danger' : 'mt-0.5 size-4 shrink-0 text-warn'} aria-hidden />
            <span className="text-ink-2">
              {l.message}
              {l.fix_hint ? <span className="block text-xs text-ink-3">{l.fix_hint}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {lints.length > 6 ? <p className="text-xs text-ink-3">…and {lints.length - 6} more in the editor’s Check tab.</p> : null}
    </section>
  )
}

function ListBlock({ title, empty, children }: { title: string; empty: string; children: React.ReactNode[] }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-3">{title}</h3>
      {children.length ? <ul className="flex flex-wrap gap-x-3 gap-y-1.5">{children}</ul> : <p className="text-sm text-ink-3">{empty}</p>}
    </section>
  )
}

function Swatch({ colour, label }: { colour: string; label: string }) {
  return (
    <li className="inline-flex items-center gap-1.5 text-sm text-ink-2">
      <span className="size-2.5 rounded-full" style={{ background: colour }} aria-hidden />
      {label}
    </li>
  )
}
